/**
 * Team Radar Component
 * Real-time team collaboration and status monitoring
 */

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Progress } from '../ui/progress'
import { cn } from '../../lib/utils'
import { 
  Users, Circle, AlertCircle, CheckCircle, Clock,
  MessageCircle, HelpCircle, Zap, Coffee, Focus,
  UserCheck, UserX, Activity, MoreVertical,
  ChevronRight, Heart, HandHelping
} from 'lucide-react'

interface TeamMember {
  id: string
  name: string
  avatar?: string
  status: string
  statusText: string
  currentFocus?: string
  needsHelp: boolean
  workload: number
  availableToHelp: boolean
  lastActive?: Date
  completedToday?: number
  inProgressTasks?: number
}

interface UserStatus {
  status: string
  currentFocus?: string
  workload: number
}

interface TeamRadarProps {
  members: TeamMember[]
  userStatus: UserStatus | null
  onMemberClick?: (memberId: string) => void
  onOfferHelp?: (memberId: string) => void
  onRequestHelp?: () => void
  compact?: boolean
}

function MemberCard({ 
  member, 
  isCurrentUser,
  onClick,
  onOfferHelp 
}: { 
  member: TeamMember
  isCurrentUser?: boolean
  onClick?: () => void
  onOfferHelp?: () => void
}) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Circle className="w-2 h-2 fill-green-500 text-green-500" />
      case 'busy': return <Circle className="w-2 h-2 fill-orange-500 text-orange-500" />
      case 'away': return <Circle className="w-2 h-2 fill-yellow-500 text-yellow-500" />
      case 'offline': return <Circle className="w-2 h-2 fill-gray-400 text-gray-400" />
      default: return <Circle className="w-2 h-2 fill-gray-400 text-gray-400" />
    }
  }

  const getWorkloadColor = (workload: number) => {
    if (workload >= 80) return 'bg-red-500'
    if (workload >= 60) return 'bg-orange-500'
    if (workload >= 40) return 'bg-blue-500'
    return 'bg-green-500'
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div 
      className={cn(
        "group relative p-3 rounded-lg border transition-all duration-200",
        "hover:shadow-md hover:border-krushr-primary/50 cursor-pointer",
        isCurrentUser && "bg-krushr-primary/5 border-krushr-primary/30",
        member.needsHelp && "border-orange-300 bg-orange-50"
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Avatar with status */}
        <div className="relative">
          <Avatar className="h-10 w-10">
            <AvatarImage src={member.avatar} />
            <AvatarFallback className="bg-krushr-primary/10 text-krushr-primary text-sm">
              {getInitials(member.name)}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border">
            {getStatusIcon(member.status)}
          </div>
        </div>

        {/* Member info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-sm font-medium text-krushr-gray-900 truncate">
                {member.name}
                {isCurrentUser && (
                  <Badge variant="outline" className="ml-2 text-xs h-4">
                    You
                  </Badge>
                )}
              </h4>
              <p className="text-xs text-krushr-gray-600 truncate">
                {member.statusText}
              </p>
            </div>

            {/* Help indicator */}
            {member.needsHelp && (
              <Badge variant="destructive" className="h-5">
                <HelpCircle className="w-3 h-3 mr-1" />
                Help
              </Badge>
            )}
          </div>

          {/* Current focus */}
          {member.currentFocus && (
            <div className="mt-2 p-2 bg-krushr-gray-50 rounded text-xs text-krushr-gray-700">
              <Focus className="w-3 h-3 inline mr-1" />
              {member.currentFocus}
            </div>
          )}

          {/* Workload bar */}
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-krushr-gray-500">Workload</span>
              <span className="font-medium">{member.workload}%</span>
            </div>
            <Progress 
              value={member.workload} 
              className="h-1.5"
              indicatorClassName={getWorkloadColor(member.workload)}
            />
          </div>

          {/* Stats */}
          {(member.completedToday !== undefined || member.inProgressTasks !== undefined) && (
            <div className="flex items-center gap-3 mt-2">
              {member.completedToday !== undefined && (
                <div className="flex items-center gap-1 text-xs text-krushr-gray-600">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span>{member.completedToday} done</span>
                </div>
              )}
              {member.inProgressTasks !== undefined && (
                <div className="flex items-center gap-1 text-xs text-krushr-gray-600">
                  <Clock className="w-3 h-3 text-blue-500" />
                  <span>{member.inProgressTasks} active</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {!isCurrentUser && (
            <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={(e) => {
                  e.stopPropagation()
                  // Open chat
                }}
              >
                <MessageCircle className="w-3 h-3 mr-1" />
                Chat
              </Button>
              
              {member.needsHelp && member.availableToHelp && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs text-krushr-primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOfferHelp?.()
                  }}
                >
                  <HandHelping className="w-3 h-3 mr-1" />
                  Offer Help
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TeamPulse({ members }: { members: TeamMember[] }) {
  const stats = useMemo(() => {
    const active = members.filter(m => m.status === 'active').length
    const busy = members.filter(m => m.status === 'busy').length
    const needHelp = members.filter(m => m.needsHelp).length
    const highWorkload = members.filter(m => m.workload >= 80).length
    
    return { active, busy, needHelp, highWorkload }
  }, [members])

  return (
    <div className="grid grid-cols-4 gap-2">
      <div className="text-center p-2 bg-green-50 rounded-lg">
        <UserCheck className="w-4 h-4 text-green-600 mx-auto mb-1" />
        <p className="text-lg font-bold text-green-600">{stats.active}</p>
        <p className="text-xs text-green-700">Active</p>
      </div>
      
      <div className="text-center p-2 bg-orange-50 rounded-lg">
        <Focus className="w-4 h-4 text-orange-600 mx-auto mb-1" />
        <p className="text-lg font-bold text-orange-600">{stats.busy}</p>
        <p className="text-xs text-orange-700">Busy</p>
      </div>
      
      <div className="text-center p-2 bg-red-50 rounded-lg">
        <HelpCircle className="w-4 h-4 text-red-600 mx-auto mb-1" />
        <p className="text-lg font-bold text-red-600">{stats.needHelp}</p>
        <p className="text-xs text-red-700">Need Help</p>
      </div>
      
      <div className="text-center p-2 bg-purple-50 rounded-lg">
        <Activity className="w-4 h-4 text-purple-600 mx-auto mb-1" />
        <p className="text-lg font-bold text-purple-600">{stats.highWorkload}</p>
        <p className="text-xs text-purple-700">High Load</p>
      </div>
    </div>
  )
}

export default function TeamRadar({
  members,
  userStatus,
  onMemberClick,
  onOfferHelp,
  onRequestHelp,
  compact = false
}: TeamRadarProps) {
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [filter, setFilter] = useState<'all' | 'available' | 'busy' | 'help'>('all')

  const filteredMembers = useMemo(() => {
    switch (filter) {
      case 'available':
        return members.filter(m => m.status === 'active' && m.workload < 60)
      case 'busy':
        return members.filter(m => m.status === 'busy' || m.workload >= 80)
      case 'help':
        return members.filter(m => m.needsHelp)
      default:
        return members
    }
  }, [members, filter])

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-krushr-primary" />
            Team Status
            <Badge variant="outline" className="ml-auto">
              {members.filter(m => m.status === 'active').length}/{members.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <TeamPulse members={members} />
          
          {/* Quick member list */}
          <div className="space-y-2">
            {members.slice(0, 3).map((member) => (
              <div 
                key={member.id}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-krushr-gray-50 cursor-pointer"
                onClick={() => onMemberClick?.(member.id)}
              >
                <Avatar className="h-6 w-6">
                  <AvatarImage src={member.avatar} />
                  <AvatarFallback className="text-xs">
                    {member.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-krushr-gray-700 flex-1 truncate">
                  {member.name}
                </span>
                {member.needsHelp && (
                  <HelpCircle className="w-3 h-3 text-orange-500" />
                )}
              </div>
            ))}
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full"
            onClick={() => onMemberClick?.('all')}
          >
            View All Team
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-krushr-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-krushr-primary" />
          Team Radar
        </h3>
        <div className="flex items-center gap-2">
          {/* Filter buttons */}
          <div className="flex items-center bg-krushr-gray-100 rounded-lg p-1">
            {(['all', 'available', 'busy', 'help'] as const).map((f) => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? 'default' : 'ghost'}
                onClick={() => setFilter(f)}
                className="h-6 px-2 text-xs capitalize"
              >
                {f}
              </Button>
            ))}
          </div>
          
          {/* View toggle */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setView(view === 'grid' ? 'list' : 'grid')}
            className="h-8"
          >
            {view === 'grid' ? 'List' : 'Grid'}
          </Button>
        </div>
      </div>

      {/* Team Pulse Stats */}
      <Card>
        <CardContent className="pt-4">
          <TeamPulse members={members} />
        </CardContent>
      </Card>

      {/* Members Grid/List */}
      <div className={cn(
        view === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-3" : "space-y-3"
      )}>
        {filteredMembers.map((member) => (
          <MemberCard
            key={member.id}
            member={member}
            isCurrentUser={userStatus && member.id === 'current-user'}
            onClick={() => onMemberClick?.(member.id)}
            onOfferHelp={() => onOfferHelp?.(member.id)}
          />
        ))}
      </div>

      {/* Request Help Button */}
      {userStatus && (
        <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-krushr-gray-900">Need assistance?</h4>
                <p className="text-xs text-krushr-gray-600 mt-1">
                  Signal your team that you could use some help
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-orange-300 hover:bg-orange-100"
                onClick={onRequestHelp}
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                Request Help
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}